/**
 * The search pipeline's presentation law: how results are ORDERED, when a deadline is
 * pressure, how postings collapse into companies, and which signals a row is allowed to
 * spend its tag budget on (S9 + S10).
 *
 * Pure, framework-free and i18n-free on purpose — no lit-html, no surfaces, no German. The
 * renderer maps `SignalTagCode` to copy; this module only decides WHICH signals are worth a
 * tag. The one upstream import is `tierForScore` from `@career/definition-v2`: the score→band
 * thresholds are domain law and are never re-derived here.
 *
 * `../domain` is imported TYPE-ONLY so the runtime edge stays one-way (`domain.ts` imports
 * this module for its projections).
 */

import { tierForScore } from "@career/definition-v2"
import type { MatchTier, OpportunityCardView } from "../domain"

// ─── Deadline pressure ────────────────────────────────────────────────────────

/** Deadline pressure derived from `validThrough`. `expired` is a WARNING, never an urgency. */
export type UrgencyState = "expired" | "urgent" | "soon"

export interface Urgency {
  state: UrgencyState
  /** Whole calendar days from today to the deadline (UTC); negative once it has passed. */
  days: number
  /** The normalized `YYYY-MM-DD` the deadline falls on. */
  date: string
}

const MS_PER_DAY = 86_400_000
const DAY_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/

/**
 * The UTC midnight of the calendar day a `YYYY-MM-DD` or full ISO timestamp denotes, or
 * null when the value is not a date at all. Time of day is deliberately ignored: a deadline
 * is a day, not an instant, and no user thinks in the posting's timezone.
 */
function utcDayOf(value: string): number | null {
  const m = DAY_PREFIX.exec(value.trim())
  if (!m) return null
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const ts = Date.UTC(year, month - 1, day)
  const back = new Date(ts)
  // Rejects 2026-13-45 and friends — Date.UTC would silently roll them over.
  if (back.getUTCFullYear() !== year || back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day) return null
  return ts
}

const isoDay = (ts: number): string => new Date(ts).toISOString().slice(0, 10)

/** null when the result carries no parsable deadline. `now` is injected so this is testable. */
export function urgencyOf(o: OpportunityCardView, now: Date = new Date()): Urgency | null {
  const raw = o.validThrough
  if (typeof raw !== "string" || raw === "") return null
  const deadline = utcDayOf(raw)
  if (deadline === null) return null
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  const days = Math.round((deadline - today) / MS_PER_DAY)
  const date = isoDay(deadline)
  if (days < 0) return { state: "expired", days, date }
  if (days <= 7) return { state: "urgent", days, date }
  if (days <= 21) return { state: "soon", days, date }
  return null
}

// ─── Ordering ─────────────────────────────────────────────────────────────────

const TIER_RANK: Record<MatchTier, number> = { unsuited: 0, weak: 1, viable: 2, strong: 3 }
const tierRank = (tier: MatchTier | undefined): number => (tier ? TIER_RANK[tier] : -1)
/** A result is graded only once it carries a real number — a live run streams results in ungraded. */
const isMatched = (o: OpportunityCardView): boolean => typeof o.score === "number" && Number.isFinite(o.score)

/**
 * THE ordering truth: evaluation first. Matched before unmatched (an ungraded result cannot
 * claim a rank it has not earned), then score desc, then tier as a tiebreak that can only
 * agree with the score, then recency, then id — total, stable and antisymmetric, so a
 * re-render never reshuffles.
 *
 * Urgency deliberately does NOT enter here: it is a separate band, not a thumb on the
 * evaluation scale (report §1.2).
 */
export function compareOpportunities(a: OpportunityCardView, b: OpportunityCardView): number {
  const [am, bm] = [isMatched(a), isMatched(b)]
  if (am !== bm) return am ? -1 : 1
  if (am && bm && a.score !== b.score) return (b.score ?? 0) - (a.score ?? 0)
  const byTier = tierRank(b.tier) - tierRank(a.tier)
  if (byTier !== 0) return byTier
  const byRecency = b.createdAt.localeCompare(a.createdAt)
  if (byRecency !== 0) return byRecency
  return a.id.localeCompare(b.id)
}

// ─── Grouping by company identity ─────────────────────────────────────────────

export interface CompanyIdentity {
  key: string
  name: string
  logoUrl?: string
  domains: readonly string[]
  sourcePlatform?: string
}

export interface OpportunityGroup {
  key: string
  company: CompanyIdentity
  items: readonly OpportunityCardView[]
}

/**
 * Group by IDENTITY, never by the employer string — two spellings of one company mint two
 * identities (report §1.3). The fallback chain stays inside normalized identities: the global
 * `companyKey`, else the normalized Org slug, else the posting's own id, which makes a keyless
 * posting a group of one. Never merging is the safe failure; merging two companies under one
 * logo is not.
 */
const groupKeyOf = (o: OpportunityCardView): string =>
  o.companyKey ?? (o.orgRef ? `org:${o.orgRef}` : `opp:${o.id}`)

/**
 * The group's source platform, but only when every posting in it agrees — a group whose
 * postings came from different boards must not claim one.
 */
function sharedSourcePlatform(items: readonly OpportunityCardView[]): string | undefined {
  const first = items[0]?.sourcePlatform
  if (!first) return undefined
  return items.every((o) => o.sourcePlatform === first) ? first : undefined
}

function identityOf(key: string, items: readonly OpportunityCardView[]): CompanyIdentity {
  const platform = sharedSourcePlatform(items)
  const withPlatform = platform ? { sourcePlatform: platform } : {}
  // Prefer the GLOBAL digest (it carries the shared name and the imagery); items are already
  // sorted, so "the first item that has one" is deterministic and prefers the best posting's.
  const digest = items.find((o) => o.company)?.company
  if (digest) {
    return {
      key,
      name: digest.name,
      domains: digest.domains,
      ...(digest.logoUrl ? { logoUrl: digest.logoUrl } : {}),
      ...withPlatform,
    }
  }
  const best = items[0]
  return {
    key,
    name: best?.org?.name ?? best?.employer ?? key,
    domains: best?.org?.domains ?? [],
    ...withPlatform,
  }
}

/** Group by IDENTITY, never by the employer string. See report §1.3. */
export function groupByCompany(list: readonly OpportunityCardView[]): OpportunityGroup[] {
  const buckets = new Map<string, OpportunityCardView[]>()
  for (const o of list) {
    const key = groupKeyOf(o)
    const bucket = buckets.get(key)
    if (bucket) bucket.push(o)
    else buckets.set(key, [o])
  }
  const built = [...buckets.entries()].flatMap(([key, items]): { group: OpportunityGroup; best: OpportunityCardView }[] => {
    const sorted = [...items].sort(compareOpportunities)
    const best = sorted[0]
    if (!best) return [] // unreachable: a bucket exists only because something was pushed into it
    return [{ group: { key, company: identityOf(key, sorted), items: sorted }, best }]
  })
  // A group sits where its BEST posting ranks; the key breaks ties so equal inputs are deterministic.
  return built
    .sort((a, b) => compareOpportunities(a.best, b.best) || a.group.key.localeCompare(b.group.key))
    .map(({ group }) => group)
}

// ─── Signal tags ──────────────────────────────────────────────────────────────

export type SignalTagKind = "warn" | "strength" | "fact"

export type SignalTagCode =
  | "source-suspicious"
  | "not-real"
  | "closed"
  | "stale"
  | "out-of-scope"
  | "expired"
  | "deadline"
  | "anti-values"
  | "low-confidence"
  | "fit-borderline"
  | "fit-mismatch"
  | "dim-fit"
  | "dim-social"
  | "dim-motivational"
  | "dim-goal"
  | "salary"

export interface SignalTag {
  kind: SignalTagKind
  code: SignalTagCode
  /** hover/label data the renderer interpolates; NO i18n here */
  detail?: string
  items?: readonly string[]
  days?: number
  date?: string
}

const WARN_CAP = 3
const STRENGTH_CAP = 3
const TOTAL_CAP = 5
const STRENGTH_FLOOR = 0.6

/** The four authentic-axis dimensions; anything else is unknown vocabulary and is skipped. */
const DIMENSION_CODE: Record<string, SignalTagCode | undefined> = {
  fit: "dim-fit",
  social: "dim-social",
  motivational: "dim-motivational",
  goal: "dim-goal",
}

/**
 * At most 5 tags, warnings first then strengths then facts. Signal, not inventory (P-B):
 * `fit === "match"` and `jobState === "open"` produce NOTHING — the score already said the
 * former and the latter is the default state, and that redundancy is exactly what S9 removes.
 */
export function signalTags(o: OpportunityCardView, now?: Date): SignalTag[] {
  const urgency = urgencyOf(o, now)

  const warnings: SignalTag[] = []
  const warn = (tag: SignalTag): void => {
    if (warnings.length < WARN_CAP) warnings.push(tag)
  }
  // Precedence order — the first three that fire are the three that show.
  if (o.real === false) warn({ kind: "warn", code: "not-real" })
  if (o.sourceQuality === "suspicious") warn({ kind: "warn", code: "source-suspicious" })
  if (o.fit === "mismatch") warn({ kind: "warn", code: "fit-mismatch" })
  // `current === false` outranks a lapsed deadline: the verifier READ the page and judged the
  // posting stale, which is a stronger statement than an ISO date that has gone by. A missing
  // deadline must not be the only way this reaches the person.
  if (o.current === false) warn({ kind: "warn", code: "stale" })
  if (urgency?.state === "expired") warn({ kind: "warn", code: "expired", days: urgency.days, date: urgency.date })
  if (o.jobState === "closed") warn({ kind: "warn", code: "closed" })
  // The verifier judged the role outside what the person is looking for. Ranked below the
  // hard-fit mismatch it resembles, above the soft signals.
  if (o.inScope === false) warn({ kind: "warn", code: "out-of-scope" })
  if (o.antiValueHits.length > 0) warn({ kind: "warn", code: "anti-values", items: [...o.antiValueHits] })
  if (urgency?.state === "urgent") warn({ kind: "warn", code: "deadline", days: urgency.days, date: urgency.date })
  if (typeof o.confidence === "number" && o.confidence < 0.5) warn({ kind: "warn", code: "low-confidence" })
  if (o.fit === "borderline") warn({ kind: "warn", code: "fit-borderline" })

  const strengths: SignalTag[] = o.signals
    .filter((s) => typeof s.strength === "number" && s.strength >= STRENGTH_FLOOR && DIMENSION_CODE[s.dimension])
    .sort((a, b) => b.strength - a.strength || a.dimension.localeCompare(b.dimension))
    .slice(0, STRENGTH_CAP)
    .flatMap((s): SignalTag[] => {
      const code = DIMENSION_CODE[s.dimension]
      return code ? [{ kind: "strength", code }] : []
    })

  const facts: SignalTag[] = o.salary ? [{ kind: "fact", code: "salary", detail: o.salary }] : []

  return [...warnings, ...strengths, ...facts].slice(0, TOTAL_CAP)
}

// ─── Bands ────────────────────────────────────────────────────────────────────

/**
 * The four-band colour/label key for any 0..1 strength, using the DOMAIN thresholds — the
 * overall score and the per-dimension meters obey ONE threshold table, not two.
 */
export function bandForStrength(strength: number): MatchTier {
  return tierForScore(strength)
}
